import { JsonLd } from 'components/common/JsonLd';
import { Seo } from 'components/common/Seo';
import { useProductPage } from 'hooks/pages/useProductPage';
import { useSanityProduct } from 'hooks/product/useSanityProduct';
import { useReviewedProducts } from 'hooks/reviews/useReviewedProducts';
import { useGlobals } from 'hooks/useGlobals';
import { DEFAULT_COUNTRY_CODE } from 'lib/constants/siteDefaults';
import { imageUrlBuilder } from 'lib/sanity';
import { useRouter } from 'next/router';
import { useMemo } from 'react';
import type { ShopifyProduct } from 'types/product/ShopifyProduct';
import { buildProduct, productId } from 'utils/jsonld';

interface ProductSeoProps {
  shopifyProduct: ShopifyProduct;
  slug: string;
}

export function ProductSeo({ slug, shopifyProduct }: ProductSeoProps) {
  const router = useRouter();
  const { data: productPage } = useProductPage(slug);

  const { seo } = productPage;

  const { title, images, longDescription, category } = useSanityProduct(slug, {
    required: true,
  });

  const description = seo?.description || longDescription;

  const { data: reviewedProducts } = useReviewedProducts({
    slugsToFilter: [slug],
  });

  const reviewedProduct = reviewedProducts?.[0];

  const { data: globals } = useGlobals();
  const shippingCountries = useMemo(() => {
    const codes = globals?.availableCountries.map((c) => c.isoCode) ?? [];
    // Guards against empty availableCountries from Shopify; an empty
    // applicableCountry would fail Google's validator.
    return codes.length > 0 ? codes : [DEFAULT_COUNTRY_CODE];
  }, [globals?.availableCountries]);

  const productCurrencyCode =
    shopifyProduct.priceRange.minVariantPrice.currencyCode;

  const productSchema = useMemo(() => {
    const mainImage = images[0];
    const width = 1200;
    const ratios = [1 / 1, 4 / 3, 16 / 9];
    const imagesList = mainImage
      ? ratios.map((ratio) =>
          imageUrlBuilder
            .image(mainImage)
            .width(width)
            .height(width / ratio)
            .url(),
        )
      : [];

    const ratingCount = reviewedProduct?.ratingCount ?? 0;
    const ratingAverage = reviewedProduct?.ratingAverage ?? 0;
    const aggregateRating =
      ratingCount > 0 && ratingAverage > 0
        ? { ratingValue: ratingAverage, reviewCount: ratingCount }
        : undefined;

    return buildProduct({
      shopifyProduct,
      category,
      name: seo?.title || title || '',
      description,
      images: imagesList,
      pagePath: router.asPath,
      shippingCountries,
      aggregateRating,
    });
  }, [
    images,
    shopifyProduct,
    router.asPath,
    reviewedProduct,
    seo?.title,
    title,
    description,
    shippingCountries,
    category,
  ]);

  return (
    <>
      <Seo
        seo={{
          title: seo?.title || title,
          description,
        }}
        openGraph={{
          type: 'product',
          title: seo?.title || title,
          description,
          images: [
            {
              width: 1200,
              height: 1200,
              alt: seo?.title || title,
              url: imageUrlBuilder
                .image(images[0])
                .width(1200)
                .height(1200)
                .url(),
            },
          ],
        }}
        additionalMetaTags={[
          {
            property: 'og:price:amount',
            content: (
              shopifyProduct.priceRange.minVariantPrice.amount as number
            ).toString(),
          },
          {
            property: 'og:price:currency',
            content: productCurrencyCode,
          },
          {
            property: 'og:availability',
            content: shopifyProduct.availableForSale
              ? 'in stock'
              : 'out of stock',
          },
        ]}
      />
      <JsonLd scriptKey={productId(router.asPath)} schema={productSchema} />
    </>
  );
}
